import React, { useState, useEffect } from 'react';
import {
  FolderOpen,
  Plus,
  Trash2,
  Edit3,
  Save,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Info,
  CheckCircle
} from 'lucide-react';
import api from '../utils/api';


const DefaultProjectStagesSettings = ({ user }) => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingName, setEditingName] = useState('');

  useEffect(() => {
    loadDefaultStages();
  }, []);

  const loadDefaultStages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/settings/default-stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Backend now returns array of objects with id, name, color
      const stagesData = response.data || [];
      if (stagesData.length > 0 && typeof stagesData[0] === 'object') {
        // New format: array of {id, name, color, order}
        setStages(stagesData);
      } else if (stagesData.length > 0) {
        // Old format: array of strings - convert to objects
        setStages(stagesData.map((name, idx) => ({
          name: typeof name === 'string' ? name : name.name || `Stage ${idx + 1}`,
          color: '#3B82F6',
          order: idx
        })));
      } else {
        // No stages returned, use defaults
        setStages([
          { name: 'To Do', color: '#6B7280', order: 0 },
          { name: 'In Progress', color: '#3B82F6', order: 1 },
          { name: 'Revisions', color: '#F59E0B', order: 2 },
          { name: 'Internal Review', color: '#8B5CF6', order: 3 },
          { name: 'Client Review', color: '#EC4899', order: 4 },
          { name: 'Complete', color: '#10B981', order: 5 }
        ]);
      }
    } catch (error) {
      console.error('Error loading default stages:', error);
      // Set default stages if API fails
      setStages([
        { name: 'To Do', color: '#6B7280', order: 0 },
        { name: 'In Progress', color: '#3B82F6', order: 1 },
        { name: 'Revisions', color: '#F59E0B', order: 2 },
        { name: 'Internal Review', color: '#8B5CF6', order: 3 },
        { name: 'Client Review', color: '#EC4899', order: 4 },
        { name: 'Complete', color: '#10B981', order: 5 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const saveDefaultStages = async () => {
    if (stages.length < 2) {
      alert('You must have at least 2 stages');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await api.put(`/settings/default-stages`, 
        { stages }, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      alert('Default project stages saved successfully!');
    } catch (error) {
      console.error('Error saving default stages:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save default stages. Please try again.';
      alert(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const addStage = () => {
    const newStage = {
      name: `Stage ${stages.length + 1}`,
      color: '#3B82F6',
      order: stages.length
    };
    setStages([...stages, newStage]);
    setEditingIndex(stages.length);
    setEditingName(newStage.name);
  };

  const removeStage = (index) => {
    if (stages.length <= 2) {
      alert('You must have at least 2 stages');
      return;
    }
    
    const stageName = stages[index]?.name || stages[index];
    if (confirm(`Are you sure you want to remove the "${stageName}" stage?`)) {
      const newStages = stages.filter((_, i) => i !== index);
      setStages(newStages);
      
      if (editingIndex === index) {
        setEditingIndex(null);
        setEditingName('');
      } else if (editingIndex > index) {
        setEditingIndex(editingIndex - 1);
      }
    }
  };

  const startEditing = (index) => {
    setEditingIndex(index);
    const stageName = stages[index]?.name || stages[index];
    setEditingName(stageName);
  };

  const saveEdit = () => {
    if (!editingName.trim()) {
      alert('Stage name cannot be empty');
      return;
    }
    
    const newStages = [...stages];
    // Update the name while preserving id and color
    if (typeof newStages[editingIndex] === 'object') {
      newStages[editingIndex] = {
        ...newStages[editingIndex],
        name: editingName.trim()
      };
    } else {
      newStages[editingIndex] = editingName.trim();
    }
    setStages(newStages);
    setEditingIndex(null);
    setEditingName('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingName('');
  };

  const moveStage = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === stages.length - 1)
    ) {
      return;
    }

    const newStages = [...stages];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newStages[index], newStages[targetIndex]] = [newStages[targetIndex], newStages[index]];
    setStages(newStages);

    // Update editing index if currently editing
    if (editingIndex === index) {
      setEditingIndex(targetIndex);
    } else if (editingIndex === targetIndex) {
      setEditingIndex(index);
    }
  };

  const resetToDefaults = () => {
    if (confirm('Are you sure you want to reset to the default project stages? This will replace all your custom stages.')) {
      setStages([
        { name: 'To Do', color: '#6B7280', order: 0 },
        { name: 'In Progress', color: '#3B82F6', order: 1 },
        { name: 'Revisions', color: '#F59E0B', order: 2 },
        { name: 'Internal Review', color: '#8B5CF6', order: 3 },
        { name: 'Client Review', color: '#EC4899', order: 4 },
        { name: 'Complete', color: '#10B981', order: 5 }
      ]);
      setEditingIndex(null);
      setEditingName('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Default Project Stages</h2>
          <p className="text-gray-600">Set default stages used for new projects</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={resetToDefaults}
            disabled={saving || loading}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset to Defaults</span>
          </button>
          <button
            onClick={saveDefaultStages}
            disabled={saving || loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all shadow-md"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900">How Default Stages Work</h4>
            <ul className="mt-2 text-sm text-blue-800 space-y-1">
              <li>• These stages will be used for new projects by default</li>
              <li>• Project templates can override these default stages</li>
              <li>• Individual projects can have custom stages that override both defaults and templates</li>
              <li>• You need at least 2 stages</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-2 text-gray-600">Loading default stages...</span>
        </div>
      )}

      {/* Stages List */}
      {!loading && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Project Stages</h3>
              <button
                onClick={addStage}
                disabled={saving}
                className="flex items-center space-x-2 px-3 py-2 text-purple-600 hover:text-purple-700 text-sm border border-purple-200 rounded hover:bg-purple-50 disabled:opacity-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Stage</span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {stages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No stages defined</p>
                <p className="text-sm">Add your first stage to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stages.map((stage, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {/* Stage Order */}
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => moveStage(index, 'up')}
                        disabled={index === 0 || saving}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveStage(index, 'down')}
                        disabled={index === stages.length - 1 || saving}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Stage Number */}
                    <div className="flex items-center justify-center w-8 h-8 bg-purple-100 text-purple-700 rounded-full font-semibold text-sm">
                      {index + 1}
                    </div>

                    {/* Stage Name */}
                    <div className="flex-1">
                      {editingIndex === index ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                saveEdit();
                              } else if (e.key === 'Escape') {
                                cancelEdit();
                              }
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            autoFocus
                            placeholder="Enter stage name..."
                          />
                          <button
                            onClick={saveEdit}
                            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          >
                            <AlertCircle className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="font-medium text-gray-900">{stage?.name || stage}</span>
                      )}
                    </div>

                    {/* Actions */}
                    {editingIndex !== index && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => startEditing(index)}
                          disabled={saving}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded transition-colors disabled:opacity-50"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeStage(index)}
                          disabled={stages.length <= 2 || saving}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-30"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Stage Count Info */}
            <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
              <span>{stages.length} stage{stages.length !== 1 ? 's' : ''} defined</span>
              <span>Minimum 2 stages required</span>
            </div>
          </div>
        </div>
      )}

      {/* Hierarchy Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-900">Stage Override Hierarchy</h4>
            <p className="mt-2 text-sm text-yellow-800">
              Project stages are determined in this order:
            </p>
            <ol className="mt-2 text-sm text-yellow-800 space-y-1 list-decimal list-inside ml-4">
              <li><strong>Individual Project Settings</strong> - Custom stages set for specific projects</li>
              <li><strong>Project Template Settings</strong> - Stages defined in project templates</li>
              <li><strong>Default Workspace Settings</strong> - These default stages you're configuring here</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefaultProjectStagesSettings;